import {Button} from '@wandb/weave/components/Button';
import * as Dialog from '@wandb/weave/components/Dialog';
import {urlPrefixed} from '@wandb/weave/config';
import React from 'react';
import {useParams} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import {Viewer} from '../../../../state/viewer/types';
import {NW_DEFAULT_ID} from '../../../../state/workspaces/types';
import {WorkspaceViews} from '../../../../state/workspaces/workspaceViewListSelector';
import {useIsAdminModeActiveAndIsNotLocalEnv} from '../../../../util/admin';
import {projectPage} from '../../../../util/urls';
import {useViewBarContext} from '../../NamedWorkspaceViewBarContext';
import {parseAndTrackNamedWorkspaces} from '../../utils';

export const DeleteViewModalContent = () => {
  const {
    viewer,
    sharedViews,
    personalWorkspaces,
    deleteViewAndLoad,
    currentView,
  } = useViewBarContext();
  const shouldShowWarning = useIsAdminModeActiveAndIsNotLocalEnv();
  const {entityName} = useParams<{entityName: string}>();

  return (
    <Dialog.Portal>
      {/* Force overlay and modal content z-index to be higher than panel bank/panel z-indices in single run workspace */}
      <Dialog.Overlay className="z-[111]" />
      <Dialog.Content
        asChild
        className={twMerge(
          'z-[112]',
          'w-screen min-w-[18rem] px-16',
          'sm:w-[37.5rem] sm:px-32 sm:py-24',
          'left-[10%] top-[15%] translate-x-[-10%] translate-y-0',
          'sm:left-[50%] sm:top-[33%] sm:translate-x-[-50%]'
        )}
        onOpenAutoFocus={e => {
          e.preventDefault();
        }}
        onCloseAutoFocus={e => {
          e.preventDefault();
        }}>
        <div
          className={twMerge(
            'w-screen min-w-[18rem] px-16',
            'sm:w-[37.5rem] sm:px-32 sm:py-24',
            'left-[10%] top-[15%] translate-x-[-10%] translate-y-0',
            'sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]'
          )}>
          <Dialog.Title>Delete this view?</Dialog.Title>
          <Dialog.Description className="mt-14 text-base">
            You can delete this view if you believe it is no longer useful to
            you and your team. This cannot be undone.
          </Dialog.Description>
          <div className="mt-24 flex flex-col gap-8 sm:flex-row">
            <Dialog.Close asChild>
              <Button
                data-test="nw-delete-view-button"
                variant="destructive"
                className="!filter-none"
                onClick={() => {
                  if (
                    (shouldShowWarning &&
                      confirm(
                        'You are in admin mode. Are you sure you want to delete this view?'
                      )) ||
                    !shouldShowWarning
                  ) {
                    const landingWorkspace = getLandingWorkspace(
                      personalWorkspaces,
                      sharedViews,
                      viewer
                    );
                    parseAndTrackNamedWorkspaces({
                      action: 'Delete view',
                      location: 'modal',
                      currentView,
                      viewer,
                    });
                    deleteViewAndLoad?.({
                      viewCid: landingWorkspace?.cid,
                      nwId: landingWorkspace?.namedWorkspaceId,
                    });

                    if (!landingWorkspace) {
                      window.location.replace(
                        urlPrefixed(projectPage(entityName))
                      );
                    }
                  }
                }}>
                Delete view
              </Button>
            </Dialog.Close>
            <Dialog.Close asChild>
              <Button variant="ghost" className="!filter-none">
                Cancel
              </Button>
            </Dialog.Close>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );
};

function getLandingWorkspace(
  personalWorkspaces: WorkspaceViews,
  sharedViews: WorkspaceViews,
  viewer: Viewer | undefined
) {
  const userName = viewer?.username;
  if (userName) {
    const userWorkspace = personalWorkspaces[userName];
    if (userWorkspace) {
      return userWorkspace;
    }
  }

  const defaultView = sharedViews[NW_DEFAULT_ID];
  if (defaultView) {
    return defaultView;
  }

  if (Object.values(sharedViews).length > 0) {
    return Object.values(sharedViews)[0];
  }

  console.error('Unable to find a workspace view to load after deleting view');
  return undefined;
}
